import { useEffect, useRef, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Play, Pause, Tag, Trash2 } from 'lucide-react';
import { useTenant } from '../components/TenantContext.jsx';
import { callsApi, tagsApi } from '../api/calls.js';
import { useFreshAudioSrc } from '../hooks/useFreshAudioSrc.js';
import { PageHeader, SectionHeader } from '../components/ui/PageHeader.jsx';
import { Card, DirectionChip } from '../components/ui/Card.jsx';
import { Button } from '../components/ui/Button.jsx';

export default function CallDetailPage() {
    const { t } = useTranslation('calls');
    // 超管路徑 /admin/tenants/:hostId/calls/:id 帶 route param 覆寫
    const { id, hostId: routeHostId } = useParams();
    const { hostId: ctxHostId, isAdmin, canOperate } = useTenant();
    const hostId = routeHostId || ctxHostId;
    const callsListPath = routeHostId ? `/admin/tenants/${routeHostId}/calls` : '/calls';
    const nav = useNavigate();
    const [call, setCall] = useState(null);
    const [tags, setTags] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!hostId || !id) return;
        let cancelled = false;
        (async () => {
            setLoading(true);
            try {
                const [r, t] = await Promise.all([
                    callsApi.get(hostId, id),
                    tagsApi.list(hostId).catch(() => ({ data: [] })),
                ]);
                if (cancelled) return;
                setCall(r.data);
                setTags(t.data || []);
            } catch (e) {
                if (!cancelled) setError(e);
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [hostId, id]);

    async function toggleTag(tagId) {
        const tagIds = call.tagIds || [];
        const next = tagIds.includes(tagId)
            ? tagIds.filter((x) => x !== tagId)
            : [...tagIds, tagId];
        setCall((c) => ({ ...c, tagIds: next }));
        await callsApi.setTags(hostId, id, next).catch((e) => setError(e));
    }

    if (loading) return <div className="text-ink-400 text-sm">{t('common:state.loading')}</div>;
    if (error) return <div className="text-danger-500 text-sm">{error.message}</div>;
    if (!call) return null;

    const startedAt = call.startedAt?._seconds
        ? new Date(call.startedAt._seconds * 1000)
        : null;
    const endedAt = call.endedAt?._seconds
        ? new Date(call.endedAt._seconds * 1000)
        : null;

    return (
        <div className="max-w-4xl">
            <PageHeader
                eyebrow={
                    call.avatarName
                        ? `${call.avatarName} · call ${call.id.slice(0, 8)}`
                        : `call · ${call.id.slice(0, 8)}`
                }
                title={
                    <span className="flex items-center gap-3">
                        <DirectionChip direction={call.direction} />
                        {call.callerNumber || call.calleeNumber || '—'}
                    </span>
                }
                description={`${startedAt ? startedAt.toLocaleString('zh-TW') : '—'} · ${t('detail.durationLabel', { duration: formatDuration(call.durationSec, t) })}${call.avatarName ? ` · ${t('detail.answeredBy', { name: call.avatarName })}` : ''}`}
                actions={
                    <>
                        <Link to={callsListPath}>
                            <Button variant="ghost"><ArrowLeft size={14} /> {t('common:actions.back')}</Button>
                        </Link>
                        {isAdmin && <Button
                            variant="outline"
                            onClick={async () => {
                                if (!confirm(t('dialog.deleteConfirm'))) return;
                                try {
                                    const r = await callsApi.remove(hostId, id);
                                    if (r && r.recordingDeleted === false) {
                                        alert(t('toast.recordingDeleteFailed', { msg: r.recordingError || '' }));
                                    }
                                    nav('/calls', { replace: true });
                                } catch (e) {
                                    alert(t('toast.deleteFailed', { msg: e.message }));
                                }
                            }}
                        >
                            <Trash2 size={14} className="text-danger-500" /> {t('common:actions.delete')}
                        </Button>}
                    </>
                }
            />

            <div className="grid grid-cols-3 gap-6">
                <div className="col-span-2 space-y-6">
                    {/* Audio */}
                    {call.audioUrl ? (
                        <Card>
                            <SectionHeader title={t('section.recording')} />
                            <AudioPlayer
                                src={call.audioUrl}
                                refresh={() => callsApi.get(hostId, id).then((r) => r?.data?.audioUrl || null)}
                            />
                            {/* SRA R11：audioUrl 現在是會過期的簽章網址，不顯示出來給人複製 */}
                            {call.audioKey && (
                                <div className="mt-3 mono-caption">{call.audioKey}</div>
                            )}
                        </Card>
                    ) : (
                        <Card className="bg-cream-100 border-cream-200">
                            <div className="text-sm text-ink-400 italic text-center py-4">
                                {t('recording.pendingPatch')}
                            </div>
                        </Card>
                    )}

                    {/* Summary */}
                    <Card>
                        <SectionHeader
                            title={t('section.aiSummary')}
                            description={
                                call.summaryGeneratedAt?._seconds
                                    ? `${new Date(call.summaryGeneratedAt._seconds * 1000).toLocaleString()}`
                                    : null
                            }
                        />
                        {call.summary ? (
                            <p className="text-sm text-ink-700 leading-relaxed whitespace-pre-wrap">
                                {call.summary}
                            </p>
                        ) : (
                            <div className="text-sm text-ink-400 italic">{t('summary.notGenerated')}</div>
                        )}
                    </Card>

                    {/* Transcript：有 fullTranscript（錄音轉錄的乾淨全程版，3 角色 ai/caller/agent）就優先顯示，
                        否則退回即時版（transcript AI 段 + transferTranscript 轉接後段接續） */}
                    {(call.fullTranscript?.length > 0 || call.transcript?.length > 0 || call.transferTranscript?.length > 0) && (
                        <Card>
                            <SectionHeader
                                title={t('section.transcript')}
                                description={t('transcript.countOnly', {
                                    count: call.fullTranscript?.length > 0
                                        ? call.fullTranscript.length
                                        : (call.transcript?.length || 0) + (call.transferTranscript?.length || 0),
                                })}
                            >
                                <span className={[
                                    'inline-block text-[10px] font-medium px-2 py-0.5 rounded-full whitespace-nowrap',
                                    call.fullTranscript?.length > 0
                                        ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                                        : 'bg-amber-50 text-amber-700 border border-amber-200',
                                ].join(' ')}>
                                    {call.fullTranscript?.length > 0 ? t('transcript.statusClean') : t('transcript.statusLive')}
                                </span>
                            </SectionHeader>
                            <div className="space-y-3 max-h-96 overflow-y-auto pr-2">
                                {call.fullTranscript?.length > 0 ? (
                                    call.fullTranscript.map((tr, i) => (
                                        <TranscriptLine key={`f${i}`} role={tr.role} text={tr.text} />
                                    ))
                                ) : (
                                    <>
                                        {(call.transcript || []).map((tr, i) => (
                                            <TranscriptLine key={`a${i}`} role={tr.role === 'user' ? 'user' : 'ai'} text={tr.text} />
                                        ))}
                                        {call.transferTranscript?.length > 0 && (
                                            <>
                                                <div className="flex items-center gap-2 py-1 text-[10px] font-mono uppercase tracking-wider text-emerald-600/80">
                                                    <span className="flex-1 h-px bg-emerald-200" />
                                                    {t('transcript.afterTransfer')}
                                                    <span className="flex-1 h-px bg-emerald-200" />
                                                </div>
                                                {call.transferTranscript.map((tr, i) => (
                                                    <TranscriptLine key={`t${i}`} role={tr.role === 'agent' ? 'agent' : 'caller'} text={tr.text} />
                                                ))}
                                            </>
                                        )}
                                    </>
                                )}
                            </div>
                        </Card>
                    )}

                </div>

                {/* Right column */}
                <div className="space-y-6">
                    <Card>
                        <SectionHeader title={t('section.basicInfo')} />
                        <dl className="space-y-3 text-sm">
                            <Row label="sessionId" mono value={call.sessionId} />
                            <Row label="avatar" mono value={call.avatarId} />
                            <Row label="DID" value={call.did} />
                            <Row label="ext" value={call.ext} />
                            <Row label={t('meta.startedAt')} value={startedAt?.toLocaleString()} />
                            <Row label={t('meta.endedAt')} value={endedAt?.toLocaleString()} />
                            <Row label={t('col.duration')} value={formatDuration(call.durationSec, t)} />
                            <Row label={t('meta.terminationType')} value={call.terminationType} />
                            <Row label={t('meta.terminationReason')} value={call.terminationReason} />
                        </dl>
                    </Card>

                    <Card>
                        <SectionHeader
                            title={t('section.tags')}
                            description={t('tags.toggleHint')}
                        />
                        {tags.length === 0 ? (
                            <div className="text-xs text-ink-400 italic">
                                {t('tags.emptyBefore')}<Link to="/tags" className="underline text-accent-700">{t('tags.emptyLink')}</Link>{t('tags.emptyAfter')}
                            </div>
                        ) : (
                            <div className="flex flex-wrap gap-2">
                                {tags.map((t) => {
                                    const active = (call.tagIds || []).includes(t.id);
                                    return (
                                        <button
                                            key={t.id}
                                            onClick={() => toggleTag(t.id)}
                                            disabled={!canOperate}
                                            className={[
                                                'inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border transition disabled:cursor-default',
                                                active
                                                    ? 'bg-accent-500 text-white border-accent-500'
                                                    : 'bg-white text-ink-700 border-cream-300 hover:border-accent-300',
                                            ].join(' ')}
                                            style={
                                                active && t.color
                                                    ? { background: t.color, borderColor: t.color }
                                                    : undefined
                                            }
                                        >
                                            <Tag size={11} />
                                            {t.name}
                                        </button>
                                    );
                                })}
                            </div>
                        )}
                    </Card>
                </div>
            </div>
        </div>
    );
}

const ROLE_CLS = {
    user: 'text-accent-700',
    ai: 'text-ink-400',
    caller: 'text-accent-700',
    agent: 'text-emerald-600',
};

function roleLabel(role, t) {
    if (role === 'caller') return t('role.caller');
    if (role === 'agent') return t('role.agent');
    if (role === 'user') return 'user';
    return 'ai';
}

function TranscriptLine({ role, text }) {
    const { t } = useTranslation('calls');
    const cls = ROLE_CLS[role] || ROLE_CLS.ai;
    return (
        <div className="text-sm">
            <span className={['inline-block w-12 font-mono text-[10px] uppercase tracking-wide mr-3 mt-0.5', cls].join(' ')}>
                {roleLabel(role, t)}
            </span>
            <span className="text-ink-700">{text}</span>
        </div>
    );
}

function Row({ label, value, mono }) {
    return (
        <div className="flex items-baseline justify-between gap-3 border-b border-cream-100 pb-2 last:border-0 last:pb-0">
            <dt className="font-mono text-[10px] uppercase tracking-wider text-ink-400 shrink-0">
                {label}
            </dt>
            <dd
                className={[
                    'text-right text-ink-700 truncate',
                    mono ? 'font-mono text-xs' : '',
                ].join(' ')}
                title={value}
            >
                {value || <span className="text-ink-300">—</span>}
            </dd>
        </div>
    );
}

function AudioPlayer({ src, refresh }) {
    const { t } = useTranslation('calls');
    const audioRef = useRef(null);
    const fresh = useFreshAudioSrc(src, refresh);   // 網址 10 分鐘過期 → 出錯自動重拿
    const [playing, setPlaying] = useState(false);
    const [progress, setProgress] = useState(0);
    const [duration, setDuration] = useState(0);
    const [rate, setRate] = useState(1);

    function toggle() {
        const a = audioRef.current;
        if (!a) return;
        if (a.paused) a.play();
        else a.pause();
    }

    function changeRate() {
        const next = rate === 1 ? 1.25 : rate === 1.25 ? 1.5 : rate === 1.5 ? 2 : 1;
        setRate(next);
        if (audioRef.current) audioRef.current.playbackRate = next;
    }

    return (
        <div>
            <audio
                ref={audioRef}
                src={fresh.src}
                onError={fresh.onError}
                onPlay={() => setPlaying(true)}
                onPause={() => setPlaying(false)}
                onTimeUpdate={(e) => setProgress(e.target.currentTime)}
                onLoadedMetadata={(e) => { setDuration(e.target.duration); fresh.onLoadedMetadata(e); }}
                preload="metadata"
            />
            <div className="flex items-center gap-4">
                <Button variant="accent" onClick={toggle}>
                    {playing ? <Pause size={16} /> : <Play size={16} />}
                    {playing ? t('audio.pause') : t('audio.play')}
                </Button>
                <div className="flex-1">
                    <div className="h-1.5 bg-cream-200 rounded-full overflow-hidden">
                        <div
                            className="h-full bg-accent-500 transition-[width]"
                            style={{ width: duration ? `${(progress / duration) * 100}%` : 0 }}
                        />
                    </div>
                    <div className="flex justify-between mt-1 mono-caption">
                        <span>{formatTime(progress)}</span>
                        <span>{formatTime(duration)}</span>
                    </div>
                </div>
                <button
                    onClick={changeRate}
                    className="font-mono text-xs px-2 py-1 bg-cream-100 hover:bg-cream-200 text-ink-700 rounded transition tnum"
                >
                    {rate}×
                </button>
            </div>
        </div>
    );
}

function formatDuration(sec, t) {
    if (!sec || sec < 0) return '—';
    const m = Math.floor(sec / 60);
    const s = sec % 60;
    return t('duration.minSec', { m, s: String(s).padStart(2, '0') });
}
function formatTime(sec) {
    if (!sec) return '0:00';
    const m = Math.floor(sec / 60);
    const s = Math.floor(sec % 60);
    return `${m}:${String(s).padStart(2, '0')}`;
}
